/**
 * LoginModal — Pippit/CapCut-style split popup for sign-in / sign-up.
 *
 * Layout:
 *   ┌─────────────────────────────────┐
 *   │  [staged photo]  │  [auth form] │
 *   │  benefits over    │   tabs      │
 *   │  gradient overlay │   form      │
 *   └─────────────────────────────────┘
 *
 * Built on shadcn/Radix `Dialog` so we get focus-trap, ESC-to-close,
 * backdrop-click-to-close, and a11y for free.
 *
 * Wires:
 *   - LandingPage owns `showLogin` state. Modal `open` ← that state.
 *   - When user closes (ESC / backdrop / X) → onClose() called → LandingPage
 *     pushes `/` URL and sets showLogin = false.
 *   - Tab switching is handled inside LoginViewContent; the parent doesn't
 *     need to track tab state.
 */

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LoginViewContent } from '@/components/views/LoginView';
import { EstageLogo } from './EstageLogo';

interface LoginModalProps {
  open: boolean;
  initialTab?: 'signin' | 'signup';
  onClose: () => void;
  onForgotPassword?: () => void;
}

interface Benefit {
  title: string;
  subline: string;
}

/** Benefits chosen for Makler audience — matches plan deliverable. */
const BENEFITS: Benefit[] = [
  { title: 'KI-Staging in Sekunden', subline: 'Leere Räume in unter 60s möbliert' },
  { title: 'Profi-Bilder ohne Fotograf', subline: 'Hochwertige Optik, ohne Studio-Termin' },
  { title: '3D-Grundrisse aus Skizzen', subline: 'Aus 2D-Plänen werden Hochglanz-Visualisierungen' },
  { title: 'DSGVO-konform & Made in Germany', subline: 'Hosted & gebaut für deutsche Makler' },
];

/**
 * Background images that crossfade behind the benefits panel — picked for
 * visual variety (different rooms / styles) so the panel feels alive without
 * being distracting.
 */
const BG_IMAGES = [
  '/landing/ES_IMG_Web_Wohnzimmer1_industrial.webp',
  '/landing/ES_IMG_Web_Wohnzimmer1_modern.webp',
  '/landing/ES_IMG_Web_Wohnzimmer1_luxus.webp',
  '/landing/ES_IMG_Web_Schlafzimmer_eingerichtet.webp',
];
const BG_ROTATION_MS = 5000;

const SparkleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
  </svg>
);

export const LoginModal: React.FC<LoginModalProps> = ({
  open,
  initialTab = 'signin',
  onClose,
  onForgotPassword,
}) => {
  // Crossfade state — index of the currently-visible bg image. Only ticks
  // while the modal is open (no point rotating images behind the scenes).
  const [bgIndex, setBgIndex] = useState(0);
  useEffect(() => {
    if (!open) return;
    const t = setInterval(() => {
      setBgIndex((i) => (i + 1) % BG_IMAGES.length);
    }, BG_ROTATION_MS);
    return () => clearInterval(t);
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Radix calls onOpenChange(false) for ESC / backdrop / X
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="lp-login-modal-content"
        // Avoid the default max-width / padding so we can run our split layout
        // edge-to-edge. The overlay + close button are still provided by Radix.
      >
        {/* Hidden title/description for screen readers — Radix requires them */}
        <DialogTitle className="sr-only">
          {initialTab === 'signup' ? 'Konto erstellen' : 'Anmelden'}
        </DialogTitle>
        <DialogDescription className="sr-only">
          Sign-up- und Login-Dialog für estage.ai. ESC oder Klick außerhalb schließt das Fenster.
        </DialogDescription>

        <div className="lp-login-modal">
          {/* ───────────── LEFT: Hero image (crossfading) + benefits ───────────── */}
          <aside className="lp-login-modal-aside">
            {/* Stack of images, only the active one is at full opacity */}
            {BG_IMAGES.map((src, i) => (
              <img
                key={src}
                src={src}
                alt=""
                aria-hidden="true"
                className={`lp-login-modal-aside-bg${i === bgIndex ? ' active' : ''}`}
              />
            ))}
            <div className="lp-login-modal-aside-overlay" aria-hidden="true" />

            <div className="lp-login-modal-aside-content">
              <div className="lp-login-modal-aside-badge">
                <span>Vorteile von Estage</span>
              </div>

              <ul className="lp-login-modal-benefits">
                {BENEFITS.map((b) => (
                  <li key={b.title}>
                    <span className="lp-login-modal-benefit-icon">
                      <SparkleIcon />
                    </span>
                    <div>
                      <div className="lp-login-modal-benefit-title">{b.title}</div>
                      <div className="lp-login-modal-benefit-subline">{b.subline}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* ───────────── RIGHT: Auth form ───────────── */}
          <section className="lp-login-modal-main">
            {/* Tiny logo — like the Pippit reference */}
            <div className="lp-login-modal-brand">
              <EstageLogo />
            </div>

            <LoginViewContent
              embedded
              initialTab={initialTab}
              onForgotPassword={onForgotPassword}
              onAuthSuccess={onClose}
            />
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
